import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '@/components/layout/Sidebar';
import Header from '@/components/layout/Header';
import TabHost from '@/app/tabs/TabHost';
import TabBar from '@/app/tabs/TabBar';
import TabRouteSync, { buildTabKey } from '@/app/tabs/TabRouteSync';
import { TabsProvider, useTabs } from '@/app/tabs/TabsProvider';
import AppThemeProvider from '@/app/theme/AppThemeProvider';
import CommandPalette from '@/app/shell/CommandPalette';
import { editandoTexto } from '@/app/shell/atalhos';

const AppContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const mainRef = useRef<HTMLElement | null>(null);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const { activeKey, fecharAba, tabs, updateTab } = useTabs();
  const currentKey = buildTabKey(location.pathname, location.search);

  // Posição de rolagem de cada aba. Fica num ref enquanto a aba está aberta e só vai para o estado das abas quando se sai
  // dela: gravar no estado a cada evento de rolagem re-renderizava o portal inteiro e, como a restauração rodava a cada
  // mudança das abas, puxava a tela de volta (às vezes para o topo) enquanto se rolava — sobretudo em telas que se
  // atualizam sozinhas, como o Diagnóstico Protheus (09/10/2026).
  const posicoes = useRef(new Map<string, number>());
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;

  // Restaura só quando a aba ativa muda.
  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;

    const salva = posicoes.current.get(currentKey) ?? tabsRef.current.find((tab) => tab.key === currentKey)?.scrollTop ?? 0;
    main.scrollTop = salva;
  }, [currentKey]);

  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    const mapa = posicoes.current;

    const handleScroll = () => {
      mapa.set(currentKey, main.scrollTop);
    };

    main.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      main.removeEventListener('scroll', handleScroll);
      // Saindo da aba: guarda a posição no estado (persistido), para voltar a ela depois.
      const posicao = mapa.get(currentKey);
      if (posicao !== undefined) updateTab(currentKey, { scrollTop: posicao });
    };
  }, [currentKey, updateTab]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const primaryShortcut = event.ctrlKey || event.metaKey;

      if (primaryShortcut && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setCommandPaletteOpen((current) => !current);
        return;
      }

      // Alt (⌥ no Mac) + tecla: event.code, porque no Mac ⌥W vira "∑" em event.key.
      if (!event.altKey || primaryShortcut) return;

      const digito = /^Digit([1-9])$/.exec(event.code);
      if (digito) {
        const targetTab = tabs[Number(digito[1]) - 1];
        if (targetTab) {
          event.preventDefault();
          navigate(targetTab.path);
        }
        return;
      }

      if ((event.code === 'ArrowLeft' || event.code === 'ArrowRight') && tabs.length > 1 && !editandoTexto(event.target)) {
        event.preventDefault();
        const currentIndex = Math.max(0, tabs.findIndex((tab) => tab.key === activeKey));
        const nextIndex = event.code === 'ArrowLeft'
          ? (currentIndex - 1 + tabs.length) % tabs.length
          : (currentIndex + 1) % tabs.length;
        navigate(tabs[nextIndex].path);
        return;
      }

      if (event.code === 'KeyW' && activeKey) {
        event.preventDefault();
        fecharAba(activeKey);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeKey, fecharAba, navigate, tabs]);

  return (
    <>
      <TabRouteSync />
      <CommandPalette open={commandPaletteOpen} onOpenChange={setCommandPaletteOpen} />
      <div className="flex h-screen bg-[var(--app-surface)] text-[#34343A]">
        <Sidebar />
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <Header onOpenCommandPalette={() => setCommandPaletteOpen(true)} />
          <TabBar />
          <main ref={mainRef} className="flex-1 overflow-auto bg-[var(--app-surface)]">
            <div className="mx-auto min-h-full w-full p-2 sm:p-4 md:p-5">
              <TabHost />
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

const AppShell: React.FC = () => (
  <AppThemeProvider>
    <TabsProvider>
      <AppContent />
    </TabsProvider>
  </AppThemeProvider>
);

export default AppShell;
